import { AND, HAS, NOT, RESULT, TYPE, UID, UID_IN, VAR } from 'shared/api/query/constants';
import { OR } from '@/lib/filter/scope-tree';
import { UNASSIGNED } from '@/lib/constants';
import { buildCustomFuncProgressCoursePred } from '@/api/query/custom-func-helper';
import { combine } from 'shared/api/query/filter';
import { getChildren } from '@/api/query/nebula/goal';
import { goal, goalCycle } from 'shared/api/query/configs.json';
import { reverseEdge } from 'shared/api/query/edges';
import { searchTermFilter } from '@/lib/filter/search-term';

export const createGoalCycleFilter = (selectedGoalCycles) => {
  if (selectedGoalCycles.length === 0) {
    return { filter: null, varBlocks: [] };
  }

  const cycles = selectedGoalCycles.filter((c) => c.uid !== UNASSIGNED);
  const hasUnassigned = selectedGoalCycles.find((c) => c.uid === UNASSIGNED) !== undefined;
  if (!hasUnassigned) {
    const vc = { name: 'via_goal_cycle_goals', typ: 1 };
    return {
      filter: { func: { name: UID, needsVar: [vc] } },
      varBlocks: [{
        alias: 'var',
        func: { name: UID },
        uid: cycles.map((c) => (c.uid)),
        model: goalCycle.model,
        children: [{ attr: reverseEdge(goal.edges.goalCycle), model: goal.model, var: vc.name }],
      }],
    };
  }

  if (cycles.length === 0) {
    return {
      filter: {
        op: NOT,
        child: [
          { func: { name: HAS, attr: goal.edges.goalCycle } },
        ],
      },
      varBlocks: [],
    };
  }

  return {
    filter: {
      op: OR,
      child: [
        { func: { name: UID_IN, attr: goal.edges.goalCycle, args: cycles.map((c) => ({ value: `${c.uid}` })) } },
        {
          op: NOT,
          child: [
            { func: { name: HAS, attr: goal.edges.goalCycle } },
          ],
        },
      ],
    },
    varBlocks: [],
  };
};

const getParentVarName = (level) => `parent_goals_level_${level}`;
const getChildVarName = (level) => `child_goals_level_${level}`;

const addParents = (level, maxLevel) => {
  const childNode = {
    attr: goal.edges.parents,
    model: goal.model,
    children: [{ attr: UID, var: getParentVarName(level) }],
  };
  if (level >= maxLevel) {
    return childNode;
  }
  const extraChild = addParents(level + 1, maxLevel);
  childNode.children.push(extraChild);
  return childNode;
};

const addChildren = (level, maxLevel, filter, order, pagination) => {
  const childNode = {
    attr: reverseEdge(goal.edges.parents),
    model: goal.model,
    pagination,
    filter,
    order,
    children: [
      { attr: UID, var: getChildVarName(level) },
      { attr: goal.edges.parents, model: goal.model, children: [{ attr: UID, var: `parents_of_${getChildVarName(level)}` }] },
    ],
  };
  if (level >= maxLevel) {
    return childNode;
  }
  const extraChild = addChildren(level + 1, maxLevel, filter, order, pagination);
  childNode.children.push(extraChild);
  return childNode;
};

const getChildrenFilter = (filter, searchTermFilter, goalCycleFilter, applyFilterOnFirstLevelOnly) => {
  if (applyFilterOnFirstLevelOnly) {
    return combine(AND, [searchTermFilter, goalCycleFilter]);
  }
  return combine(AND, [filter, searchTermFilter, goalCycleFilter]);
};

// When an order exists, sort by the defined order otherwise use the default sorting.
// Default sorting groups first by goal types and then uses the children order.
export const goalCascadeList = ({
  pagination,
  goalCycles = [],
  filter = null,
  varBlocks = [],
  searchTerm = '',
  progressCourse = [],
  progressCourseAlias = '',
  order = [],
  alias = RESULT,
  childrenPagination = { itemsPerPage: 10, page: 1 },
  excludedGoalIds = [],
  applyFilterOnFirstLevelOnly = false,
  childrenLevels = 1,
  parentLevels = 1,
}) => {
  const cycleGoalsVar = 'cycle_goals';
  const titleFilter = searchTermFilter({ searchTerm, edgeName: goal.edges.title });
  const { filter: goalCycleFilter, varBlocks: goalCycleFilterVarBlocks } = createGoalCycleFilter(goalCycles);
  const firstLevelFilter = combine(AND, [
    titleFilter,
    goalCycleFilter,
    filter,
  ]);

  const childrenOrder = order.filter((o) => o.attr !== goal.edges.cachedParents);

  const childrenFilter = getChildrenFilter(filter, titleFilter, goalCycleFilter, applyFilterOnFirstLevelOnly);

  const getVars = () => {
    const vars = [{ name: 'result_goals', typ: 1 }];

    for (let i = 0; i < childrenLevels; i++) {
      vars.push({ name: getChildVarName(i), typ: 1 });
      vars.push({ name: `parents_of_${getChildVarName(i)}`, typ: 1 });
    }

    for (let i = 0; i < parentLevels; i++) {
      vars.push({ name: getParentVarName(i), typ: 1 });
    }

    return vars;
  };

  const children = () => {
    const children = [{ attr: UID, var: 'result_goals' }];

    if (childrenLevels > 0) {
      const cFilter = applyFilterOnFirstLevelOnly ? null : childrenFilter;
      const childrenNode = addChildren(0, childrenLevels - 1, cFilter, childrenOrder, childrenPagination);
      children.push(childrenNode);
    }

    if (parentLevels > 0) {
      const parentsNode = addParents(0, parentLevels - 1);
      children.push(parentsNode);
    }

    return children;
  };

  const customFuncs = [];
  if (progressCourseAlias !== '') {
    customFuncs.push(buildCustomFuncProgressCoursePred({ alias: progressCourseAlias, attr: reverseEdge(goal.edges.paysOnto), timeSeries: progressCourse }));
  }

  // this is an optimization that allows us to not query using DQL 'type()'. This is especially interesting for execution with RRFX, as it makes mirror queries far less expensive.
  const cycleGoalsVarQuery = () => {
    if (goalCycleFilter != null && goalCycleFilter.func !== undefined && goalCycleFilter.func.name === UID) {
      return {
        alias: VAR,
        model: goal.model,
        func: goalCycleFilter.func,
        needsVar: goalCycleFilter.func.needsVar,
        filter: firstLevelFilter,
        children: [{ attr: UID, var: cycleGoalsVar }],
      };
    }

    return {
      alias: VAR,
      model: goal.model,
      func: { name: TYPE, args: [{ value: goal.model }] },
      filter: firstLevelFilter,
      children: [{ attr: UID, var: cycleGoalsVar }],
    };
  };

  return [
    ...goalCycleFilterVarBlocks,
    cycleGoalsVarQuery(),
    {
      alias: VAR,
      model: goal.model,
      needsVar: [{ name: cycleGoalsVar, typ: 1 }],
      func: { name: UID, needsVar: [{ name: cycleGoalsVar, typ: 1 }] },
      default: [],
      children: [
        {
          attr: goal.edges.parents,
          model: goal.model,
          filter: combine(AND, [firstLevelFilter, { func: { name: UID, needsVar: [{ name: cycleGoalsVar, typ: 1 }] } }]),
          children: [
            {
              alias: 'children',
              attr: reverseEdge(goal.edges.parents),
              model: goal.model,
              var: 'goals_with_parent',
            },
          ],
        },
      ],
    },
    {
      model: goal.model,
      alias: 'all',
      needsVar: [
        { name: cycleGoalsVar, typ: 1 },
      ],
      func: { name: UID, needsVar: [{ name: cycleGoalsVar, typ: 1 }] },
      filter: {
        op: AND,
        child: [
          {
            op: NOT,
            child: [
              { func: { name: UID, needsVar: [{ name: 'goals_with_parent', typ: 1 }] } },
            ],
          },
        ],
      },
      order,
      pagination,
      default: [],
      children: children(),
    },
    {
      model: goal.model,
      alias,
      needsVar: getVars(),
      func: {
        name: UID,
        needsVar: getVars(),
      },
      filter: excludedGoalIds.length > 0 ? { op: NOT, child: [{ func: { name: 'uid', uid: excludedGoalIds } }] } : null,
      order,
      default: [],
      children: getChildren({
        childrenFilter,
        childrenOrder,
        customFuncs,
      }),
    },
    ...varBlocks,
  ];
};
